import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import * as groupsApi from '../api/groups';
import * as schedulesApi from '../api/schedules';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import GroupForm, { groupToForm } from '../components/GroupForm';
import { ErrorAlert, RelativeTime, Spinner, UserChip, UserSearchPicker } from '../components/ui';
import { cn, ui } from '../lib/ui';

function rolePill(role) {
  if (role === 'admin') return <span className={ui.pillWarning}>Admin</span>;
  if (role === 'moderator') return <span className={ui.pillInfo}>Moderator</span>;
  return null;
}

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [sharedEvents, setSharedEvents] = useState({ loading: true, items: [] });

  const isGroupAdmin =
    group &&
    (group.createdBy === user?.userId ||
      members.some(
        m => m.userId === user?.userId && (m.role === 'admin' || m.role === 'moderator')
      ) ||
      user?.role === 'admin');

  const loadGroup = useCallback(async () => {
    try {
      const data = await groupsApi.getGroup(groupId);
      setGroup(data.group);
      setMembers(data.members || []);
    } catch (err) {
      setError(err);
    }
  }, [groupId]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  useEffect(() => {
    let cancelled = false;
    setSharedEvents({ loading: true, items: [] });
    const now = new Date();
    schedulesApi
      .listSchedules({
        startAfter: now.toISOString(),
        startBefore: new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(),
        limit: 100,
      })
      .then(d => {
        if (cancelled) return;
        const items = (d.schedules || []).filter(
          s => s.sharedWith && s.sharedWith.groups && s.sharedWith.groups.includes(groupId)
        );
        items.sort((a, b) => {
          const tA = a.startTime ? new Date(a.startTime).getTime() : 0;
          const tB = b.startTime ? new Date(b.startTime).getTime() : 0;
          return tA - tB;
        });
        setSharedEvents({ loading: false, items });
      })
      .catch(() => !cancelled && setSharedEvents({ loading: false, items: [] }));
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  async function handleUpdate(payload) {
    await groupsApi.updateGroup(groupId, payload);
    toast.success('Group updated.');
    setEditing(false);
    loadGroup();
  }

  async function handleDelete() {
    const ok = await confirm({
      title: 'Delete group?',
      message:
        'This will permanently delete the group and remove all members. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await groupsApi.deleteGroup(groupId);
      toast.success('Group deleted.');
      navigate('/groups');
    } catch (err) {
      toast.error(err);
    }
  }

  async function handleAddMember(picked) {
    try {
      await groupsApi.addMember(groupId, { userId: picked.userId });
      toast.success(`${picked.name || picked.userId} added to group.`);
      loadGroup();
    } catch (err) {
      toast.error(err);
    }
  }

  async function handleRemoveMember(memberId) {
    try {
      await groupsApi.removeMember(groupId, memberId);
      toast.success('Member removed.');
      loadGroup();
    } catch (err) {
      toast.error(err);
    }
  }

  async function handleRoleChange(memberId, newRole) {
    try {
      await groupsApi.updateMemberRole(groupId, memberId, { role: newRole });
      toast.success('Role updated.');
      loadGroup();
    } catch (err) {
      toast.error(err);
    }
  }

  if (error) return <ErrorAlert error={error} />;
  if (!group) return <Spinner />;

  const settings = group.settings || {};
  const memberUserIds = members.map(m => m.userId);

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4 items-start')}>
        <div>
          <h1 className="text-title-l">{group.name}</h1>
          {group.description && <p className={ui.muted}>{group.description}</p>}
        </div>
        {isGroupAdmin && (
          <div className={ui.row}>
            <button type="button" className={ui.btnSecondary} onClick={() => setEditing(true)}>
              Settings
            </button>
            <button type="button" className={ui.btnDanger} onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
      </div>

      {isGroupAdmin && (
        <div className={cn(ui.card, 'mb-4')}>
          <h2 className="mb-2 text-title-s">Group settings</h2>
          <div className={cn(ui.grid, ui.gridMd2)}>
            <p className="text-body-s">
              <strong>Who can post:</strong>{' '}
              {settings.whoCanPost === 'moderators_only' ? 'Moderators only' : 'All members'}
            </p>
            <p className="text-body-s">
              <strong>Who can invite:</strong>{' '}
              {settings.whoCanInvite === 'moderators_only' ? 'Moderators only' : 'All members'}
            </p>
            <p className="text-body-s">
              <strong>Who can message:</strong>{' '}
              {settings.whoCanMessage === 'moderators_only' ? 'Moderators only' : 'All members'}
            </p>
            <p className="text-body-s">
              <strong>Member approval:</strong>{' '}
              {settings.memberApproval === 'moderator_review' ? 'Moderator review' : 'Automatic'}
            </p>
          </div>
        </div>
      )}

      <h2 className="mb-2 text-title-m">Shared events</h2>
      {sharedEvents.loading ? (
        <p className={cn(ui.muted, 'mb-4')}>Loading events…</p>
      ) : sharedEvents.items.length === 0 ? (
        <p className={cn(ui.muted, 'mb-4')}>No upcoming events shared with this group.</p>
      ) : (
        <div className={cn(ui.grid, ui.gridMd2, 'mb-4')}>
          {sharedEvents.items.map(s => {
            const calDate = s.startTime ? s.startTime.slice(0, 7) : '';
            return (
              <section key={s.id} className={ui.card}>
                <h3 className={ui.h3}>{s.subject || '(no subject)'}</h3>
                <p className={cn(ui.muted, 'text-body-s')}>
                  <RelativeTime iso={s.startTime} />
                  {s.endTime && (
                    <>
                      {' '}
                      - <RelativeTime iso={s.endTime} />
                    </>
                  )}
                  {s.location ? ` - ${s.location}` : ''}
                </p>
                {s.showTimeAs && s.showTimeAs !== 'busy' && (
                  <p className="text-body-s">
                    <span className={ui.pillNeutral}>{s.showTimeAs}</span>
                  </p>
                )}
                <Link
                  className={ui.btnSecondary}
                  to={
                    calDate
                      ? `/schedules?view=month&date=${calDate}&select=${encodeURIComponent(s.id)}`
                      : `/schedules?select=${encodeURIComponent(s.id)}`
                  }
                >
                  View on calendar
                </Link>
              </section>
            );
          })}
        </div>
      )}

      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h2 className="text-title-m">Members ({members.length})</h2>
        {isGroupAdmin && (
          <button type="button" className={ui.btnPrimary} onClick={() => setAddingMember(true)}>
            Add member
          </button>
        )}
      </div>

      {members.length === 0 ? (
        <p className={ui.muted}>No members yet.</p>
      ) : (
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                {isGroupAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.userId}>
                  <td>
                    <UserChip userId={m.userId} name={m.name || m.userId} />
                  </td>
                  <td>{rolePill(m.role) || <span className={ui.muted}>Member</span>}</td>
                  {isGroupAdmin && (
                    <td>
                      <div className={ui.row}>
                        {m.role !== 'admin' && (
                          <button
                            type="button"
                            className={cn(ui.btnSecondary, ui.btnSm)}
                            onClick={() =>
                              handleRoleChange(
                                m.userId,
                                m.role === 'moderator' ? 'member' : 'moderator'
                              )
                            }
                          >
                            {m.role === 'moderator' ? 'Demote' : 'Make moderator'}
                          </button>
                        )}
                        {m.userId !== user?.userId && (
                          <button
                            type="button"
                            className={cn(ui.btnDanger, ui.btnSm)}
                            onClick={() => handleRemoveMember(m.userId)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={editing} onClose={() => setEditing(false)} title="Edit group">
        <GroupForm
          key={group.groupId}
          initial={groupToForm(group)}
          submitLabel="Save changes"
          onSubmit={handleUpdate}
          onCancel={() => setEditing(false)}
        />
      </Modal>

      <Modal open={addingMember} onClose={() => setAddingMember(false)} title="Add member">
        <UserSearchPicker
          onPick={picked => {
            handleAddMember(picked);
            setAddingMember(false);
          }}
          exclude={memberUserIds}
        />
      </Modal>
    </>
  );
}
